import {
  Button,
  Card,
  Table,
  Text,
  Title,
} from '@bancobarigui/barihaus';
import { Dropdown, Tooltip, Tag } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { MoreOutlined, DownOutlined, RightOutlined, ExclamationCircleOutlined, StarOutlined, StarFilled } from '@ant-design/icons';

import { canScheduleInspection } from '@/shared/status';
import { useInspectionsTable } from './useInspectionsTable';
import { InspectionTableData, FilterableColumn, FilterOptionsLoader } from './useInspectionsTable';
import { InspectionIssueStatus } from '@/services/types/inspection.types';
import { usePermissions } from '@/shared/hooks/usePermissions';
import { generateActionMenuItems } from './ActionMenuItems';
import { ExpandableRow } from './ExpandableRow';
import './styles.css';

interface FilterOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface InspectionsTableProps {
  inspections: InspectionTableData[];
  isLoading: boolean;
  onSchedule: (itemId: string, inspectionId: string) => void;
  onAssign: (itemId: string, inspectionId: string) => void;
  onProviderAccept?: (assignmentId: string, inspectionId: string) => void;
  onProviderReject?: (assignmentId: string, inspectionId: string) => void;
  onAssignInspector?: (assignmentId: string, inspectionId: string) => void;
  onInspectorAccept?: (assignmentId: string, inspectionId: string) => void;
  onInspectorReject?: (assignmentId: string, inspectionId: string) => void;
  onCreateIssue?: (itemId: string) => void;
  onSubmitProviderReport?: (itemId: string, inspectionId: string) => void;
  onRowClick?: (itemId: string, inspectionId: string) => void;
  onToggleFavorite?: (inspectionId: string) => void;
  currentPage?: number;
  pageSize?: number;
  totalItems?: number;
  onPageChange?: (page: number, pageSize: number) => void;
  statusOptions?: FilterOption[];
  inspectionTypeOptions?: FilterOption[];
  propertyTypeOptions?: FilterOption[];
  inspectionReasonOptions?: FilterOption[];
  providerOptions?: FilterOption[];
  columnFilters?: Partial<Record<FilterableColumn, string | undefined>>;
  onColumnFilterChange?: (field: FilterableColumn, value: string | undefined) => void;
  loadFilterOptions?: FilterOptionsLoader;
}

export function InspectionsTable({
  inspections,
  isLoading,
  onSchedule,
  onAssign,
  onProviderAccept,
  onProviderReject,
  onAssignInspector,
  onInspectorAccept,
  onInspectorReject,
  onCreateIssue,
  onSubmitProviderReport,
  onRowClick,
  onToggleFavorite,
  currentPage,
  pageSize: externalPageSize,
  totalItems,
  onPageChange,
  statusOptions,
  inspectionTypeOptions,
  propertyTypeOptions,
  inspectionReasonOptions,
  providerOptions,
  columnFilters,
  onColumnFilterChange,
  loadFilterOptions,
}: InspectionsTableProps) {
  const {
    columns: rawColumns,
  } = useInspectionsTable({
    inspections,
    onSchedule,
    onAssign,
    onToggleFavorite,
    statusOptions,
    inspectionTypeOptions,
    propertyTypeOptions,
    inspectionReasonOptions,
    providerOptions,
    columnFilters,
    onColumnFilterChange,
    loadFilterOptions,
  });

  const { hasPermission, isInspectorUser, isBankUser } = usePermissions();
  const canTogglePriority = isBankUser();

  const hasActiveIssues = (record: InspectionTableData): boolean => {
    if (!record.items || record.items.length === 0) return false;

    return record.items.some(item =>
      item.issues && item.issues.length > 0 &&
      item.issues.some(issue =>
        issue.status === InspectionIssueStatus.Aberta ||
        issue.status === InspectionIssueStatus.EmAndamento
      )
    );
  };

  const processedColumns: ColumnsType<any> = rawColumns.map((column) => {
    if (column.key === 'priority') {
      return {
        ...column,
        render: (_: any, record: InspectionTableData) => {
          const priorityData = column.render ? column.render(_, record, 0) : null;
          if (!priorityData || typeof priorityData !== 'object') return null;

          const { isFavorite, onToggle } = priorityData as { isFavorite: boolean; onToggle: () => void };

          // Para usuários de fora da camada Banco, a estrela aparece apenas como
          // indicador visual (read-only): sem clique, sem cursor de ponteiro.
          if (!canTogglePriority) {
            return (
              <div data-row-action onClick={(e) => e.stopPropagation()}>
                <Tooltip title={isFavorite ? 'Prioridade do banco' : 'Não marcado como prioridade pelo banco'}>
                  {isFavorite ? (
                    <StarFilled style={{ color: '#faad14', fontSize: '18px' }} />
                  ) : (
                    <StarOutlined style={{ color: '#d9d9d9', fontSize: '18px' }} />
                  )}
                </Tooltip>
              </div>
            );
          }

          return (
            <div
              data-row-action
              onClick={(e) => {
                e.stopPropagation();
                onToggle?.();
              }}
              className="cursor-pointer"
            >
              <Tooltip title={isFavorite ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}>
                {isFavorite ? (
                  <StarFilled style={{ color: '#faad14', fontSize: '18px' }} />
                ) : (
                  <StarOutlined style={{ color: '#d9d9d9', fontSize: '18px' }} />
                )}
              </Tooltip>
            </div>
          );
        },
      };
    }

    if (column.key === 'proposalNumber') {
      return {
        ...column,
        render: (_: any, record: InspectionTableData) => {
          return (
            <span className="inline-flex items-center gap-1.5" style={{ verticalAlign: 'middle' }}>
              {hasActiveIssues(record) && (
                <Tooltip title="Pendências ativas">
                  <ExclamationCircleOutlined className="text-orange-500" style={{ fontSize: '14px', verticalAlign: 'middle' }} />
                </Tooltip>
              )}
              <span style={{ verticalAlign: 'middle' }}>{record.proposalNumber}</span>
            </span>
          );
        },
      };
    }

    if (column.key === 'status') {
      return {
        ...column,
        render: (_: any, record: InspectionTableData) => {
          const statusData = column.render ? column.render(record.status, record, 0) : null;
          if (!statusData || typeof statusData !== 'object') return null;

          const { label, color } = statusData as { status: string; color: string; label: string };

          return (
            <Tooltip title={label}>
              <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                <Tag color={color} style={{ margin: 0, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {label}
                </Tag>
              </div>
            </Tooltip>
          );
        },
      };
    }

    if (column.key === 'actions') {
      return {
        ...column,
        render: (_: any, record: InspectionTableData) => {
          if (record.items && record.items.length > 1) {
            return <Text className="text-gray-400 text-xs">Ver itens abaixo</Text>;
          }

          const actions = column.render ? column.render(_, record, 0) : null;
          if (
            !actions ||
            typeof actions !== 'object' ||
            !('handleSchedule' in actions) ||
            !('handleAssign' in actions)
          )
            return null;

          const typedActions = actions as {
            handleSchedule: () => void;
            handleAssign: () => void;
          };

          const canSchedule = record.items?.[0]?.status !== undefined
            ? canScheduleInspection(record.items[0].status)
            : false;

          const menuItems = generateActionMenuItems({
            record,
            hasPermission,
            isInspectorUser,
            onProviderAccept,
            onProviderReject,
            onAssignInspector,
            onInspectorAccept,
            onInspectorReject,
            onCreateIssue,
            onSubmitProviderReport,
            onSchedule: canSchedule ? typedActions.handleSchedule as any : onSchedule,
            onAssign: typedActions.handleAssign as any,
          });

          if (!menuItems || menuItems.length === 0) {
            return <Text className="text-gray-400 text-xs">Sem ações disponíveis</Text>;
          }

          return (
            <div data-row-action onClick={(e) => e.stopPropagation()}>
              <Dropdown
                menu={{ items: menuItems }}
                placement="bottomRight"
                trigger={['click']}
                overlayClassName="inspection-actions-dropdown"
              >
                <Button type="link" icon={<MoreOutlined />} className="!p-0">
                  Opções
                </Button>
              </Dropdown>
            </div>
          );
        },
      };
    }

    return column;
  });

  return (
    <Card className="shadow-sm">
        <div className="flex justify-between items-center mb-4">
          <Title level={4}>Inspeções Solicitadas</Title>
          <Text className="text-gray-500">Total: {totalItems || inspections.length} inspeção(ões)</Text>
        </div>

        <div className="overflow-x-auto">
          <Table
            loading={isLoading}
            dataSource={inspections}
            columns={processedColumns}
            rowKey="key"
            scroll={{ x: 1430 }}
            onRow={(record) => {
              const typedRecord = record as InspectionTableData;
              const hasIssues = hasActiveIssues(typedRecord);
              const isFavorite = typedRecord.isFavorite;
              return {
                onClick: (e) => {
                  const target = e.target as HTMLElement;
                  if (
                    !target.closest('.ant-table-row-expand-icon') &&
                    !target.closest('[data-row-action]')
                  ) {
                    if (typedRecord.items && typedRecord.items.length === 1) {
                      const itemId = typedRecord.items[0]!.id || typedRecord.id;
                      onRowClick?.(itemId, typedRecord.id);
                    }
                  }
                },
                className: `
                  ${typedRecord['items'] && typedRecord['items'].length === 1 ? 'cursor-pointer hover:bg-gray-50' : ''}
                  ${hasIssues ? 'text-orange-600' : ''}
                  ${isFavorite ? 'favorite-row' : ''}
                `,
                title: hasIssues ? 'Esta inspeção possui pendências ativas' : (isFavorite ? 'Inspeção favorita' : undefined),
              };
            }}
            expandable={{
              expandedRowRender: (record: any) => (
                <ExpandableRow
                  record={record as InspectionTableData}
                  hasPermission={hasPermission}
                  isInspectorUser={isInspectorUser}
                  onProviderAccept={onProviderAccept}
                  onProviderReject={onProviderReject}
                  onAssignInspector={onAssignInspector}
                  onInspectorAccept={onInspectorAccept}
                  onInspectorReject={onInspectorReject}
                  onCreateIssue={onCreateIssue}
                  onSubmitProviderReport={onSubmitProviderReport}
                  onSchedule={onSchedule}
                  onAssign={onAssign}
                  onRowClick={onRowClick}
                />
              ),
              expandIcon: ({ expanded, onExpand, record }) => {
                const typedRecord = record as InspectionTableData;
                if (!typedRecord.items || typedRecord.items.length <= 1) {
                  return <span style={{ width: 16, display: 'inline-block' }} />;
                }

                return expanded ? (
                  <DownOutlined
                    onClick={(e) => {
                      e.stopPropagation();
                      onExpand(record, e);
                    }}
                    className="cursor-pointer text-blue-600"
                  />
                ) : (
                  <RightOutlined
                    onClick={(e) => {
                      e.stopPropagation();
                      onExpand(record, e);
                    }}
                    className="cursor-pointer text-blue-600"
                  />
                );
              },
              rowExpandable: (record) => {
                const typedRecord = record as InspectionTableData;
                return (typedRecord.items?.length || 0) > 1;
              },
            }}
            pagination={onPageChange ? {
              current: currentPage || 1,
              pageSize: externalPageSize || 10,
              total: totalItems || 0,
              showSizeChanger: true,
              showTotal: (total: number, range: [number, number]) => `${range[0]}-${range[1]} de ${total} itens`,
              pageSizeOptions: ['10', '20', '50', '100'],
              onChange: (page: number, size: number) => onPageChange(page, size),
              onShowSizeChange: (current: number, size: number) => onPageChange(current, size),
            } : {
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total: number, range: [number, number]) => `${range[0]}-${range[1]} de ${total} itens`,
              pageSizeOptions: ['10', '20', '50', '100'],
            }}
            locale={{
              emptyText: <Text className="text-gray-500">Nenhuma inspeção encontrada.</Text>,
            }}
          />
        </div>
    </Card>
  );
}
